import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AnalysisSummary } from '@swg/contracts';
import { canRunAnalyses } from '@swg/domain';
import { ApiError, createAnalysis, deleteAnalysis, fetchAnalyses, rerunAnalysis } from '../api.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { theme } from '../theme.js';
import { useUser } from '../user-context.js';

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

/**
 * Startscherm (US01, conform prototype): hero + directe URL-invoer om een
 * analyse te starten, met daaronder de lijst met eerdere analyses. Er is geen
 * aparte "nieuwe analyse"-pagina meer; starten kan hier meteen.
 */
export function Dashboard() {
  const user = useUser();
  const navigate = useNavigate();
  const mayRun = canRunAnalyses(user.role);

  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ready'; analyses: AnalysisSummary[] } | { status: 'error' }
  >({ status: 'loading' });
  const [busyId, setBusyId] = useState<string | null>(null);

  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAnalyses()
      .then((res) => !cancelled && setState({ status: 'ready', analyses: res.analyses }))
      .catch(() => !cancelled && setState({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const analysis = await createAnalysis({ url: url.trim() });
      navigate(`/analyses/${analysis.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Er is een fout opgetreden.');
      setSubmitting(false);
    }
  }

  const handleRerun = async (id: string) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await rerunAnalysis(id);
      navigate(`/analyses/${id}`);
    } catch {
      window.alert('Opnieuw uitvoeren is mislukt. Probeer het opnieuw.');
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (busyId) return;
    if (!window.confirm(`Analyse "${title}" en alle uitvoeringen definitief verwijderen?`)) return;
    setBusyId(id);
    try {
      await deleteAnalysis(id);
      setState((s) =>
        s.status === 'ready'
          ? { status: 'ready', analyses: s.analyses.filter((a) => a.id !== id) }
          : s,
      );
    } catch {
      window.alert('Verwijderen is mislukt. Probeer het opnieuw.');
    } finally {
      setBusyId(null);
    }
  };

  const secondaryBtn = {
    background: theme.color.surface,
    border: `1px solid ${theme.color.borderStrong}`,
    borderRadius: 7,
    padding: '6px 12px',
    fontSize: 12.5,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: theme.color.text,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      {/* Hero */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 14,
          background: theme.gradient.header,
          padding: '42px 46px',
          margin: '6px 0 24px',
          color: '#FFFFFF',
        }}
      >
        <img
          src="/assets/schoonderwolf-beeldmerk.svg"
          alt=""
          style={{
            position: 'absolute',
            right: -30,
            top: -40,
            height: 280,
            opacity: 0.12,
            transform: 'rotate(10deg)',
            filter: 'brightness(0) invert(1)',
            pointerEvents: 'none',
          }}
        />
        <h1 style={{ margin: '0 0 10px', fontSize: 27, fontWeight: 700, letterSpacing: '-0.01em', position: 'relative' }}>
          Start een concurrentieanalyse
        </h1>
        <p style={{ margin: 0, color: '#C9DAE6', fontSize: 15, lineHeight: 1.6, maxWidth: 600, position: 'relative' }}>
          Voer een bedrijfs-URL in. Er wordt automatisch een bedrijfsprofiel opgesteld, concurrenten
          geïdentificeerd en een onderbouwd concurrentierapport gegenereerd.
        </p>
      </div>

      {/* URL-invoer */}
      {mayRun ? (
        <div
          style={{
            background: theme.color.surface,
            border: `1px solid ${theme.color.border}`,
            borderRadius: 12,
            padding: 26,
            boxShadow: '0 1px 3px rgba(16,24,40,0.05)',
          }}
        >
          <label htmlFor="url" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            Bedrijfs-URL
          </label>
          <form onSubmit={onSubmit} style={{ display: 'flex', gap: 10 }}>
            <input
              id="url"
              type="text"
              inputMode="url"
              placeholder="bijv. www.voorbeeld.nl"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              style={{
                flex: 1,
                border: `1px solid ${error ? '#F2C6BE' : theme.color.borderStrong}`,
                borderRadius: 8,
                padding: '11px 14px',
                fontSize: 15,
                fontFamily: theme.font.mono,
                outline: 'none',
                color: theme.color.text,
                boxSizing: 'border-box',
              }}
            />
            <button
              type="submit"
              disabled={submitting}
              style={{
                background: theme.color.cta,
                color: theme.color.ctaText,
                border: 'none',
                borderRadius: 8,
                padding: '11px 22px',
                fontSize: 14.5,
                fontWeight: 700,
                cursor: submitting ? 'default' : 'pointer',
                opacity: submitting ? 0.7 : 1,
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              {submitting ? 'Bezig…' : 'Start analyse'}
            </button>
          </form>
          {error && (
            <div
              style={{
                marginTop: 10,
                background: '#FBE7E3',
                border: '1px solid #F2C6BE',
                color: '#A33B2E',
                borderRadius: 8,
                padding: '9px 13px',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: 12.5, color: theme.color.subtle }}>
            Alleen een openbaar bereikbare bedrijfswebsite is nodig — geen verdere configuratie.
          </div>
        </div>
      ) : (
        <div
          style={{
            background: theme.color.surface,
            border: `1px solid ${theme.color.border}`,
            borderRadius: 12,
            padding: '18px 22px',
            fontSize: 13.5,
            color: theme.color.muted,
          }}
        >
          Je hebt de rol Kijker en kunt geen analyses starten. Je kunt bestaande rapporten wel
          bekijken.
        </div>
      )}

      {/* Eerdere analyses */}
      <div style={{ marginTop: 36 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Eerdere analyses</h2>
          {state.status === 'ready' && state.analyses.length > 0 && (
            <div style={{ fontSize: 12.5, color: theme.color.subtle }}>
              {state.analyses.length} {state.analyses.length === 1 ? 'analyse' : 'analyses'}
            </div>
          )}
        </div>

        {state.status === 'loading' && <p style={{ color: theme.color.muted }}>Laden…</p>}
        {state.status === 'error' && (
          <p style={{ color: theme.color.muted }}>De analyses konden niet worden geladen.</p>
        )}

        {state.status === 'ready' && state.analyses.length === 0 && (
          <div
            style={{
              background: theme.color.surface,
              border: `1px solid ${theme.color.border}`,
              borderRadius: 12,
              padding: '32px 24px',
              textAlign: 'center',
              color: theme.color.muted,
              fontSize: 13.5,
            }}
          >
            Nog geen analyses. {mayRun ? 'Start je eerste analyse hierboven.' : ''}
          </div>
        )}

        {state.status === 'ready' && state.analyses.length > 0 && (
          <div
            style={{
              background: theme.color.surface,
              border: `1px solid ${theme.color.border}`,
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            {state.analyses.map((a) => (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  rowGap: 10,
                  flexWrap: 'wrap',
                  padding: '14px 18px',
                  borderBottom: `1px solid ${theme.color.bg}`,
                }}
              >
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {a.title}
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.subtle, fontFamily: theme.font.mono }}>
                    {a.company.normalizedDomain}
                    {a.latestRun && (
                      <span style={{ fontFamily: theme.font.body, color: theme.color.muted }}>
                        {' · '}laatste uitvoering: {formatDate(a.latestRun.createdAt)}
                      </span>
                    )}
                  </div>
                </div>
                {a.latestRun && <StatusBadge status={a.latestRun.status} />}
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => navigate(`/analyses/${a.id}`)}
                    style={secondaryBtn}
                  >
                    Open rapport
                  </button>
                  {mayRun && (
                    <button
                      type="button"
                      onClick={() => void handleRerun(a.id)}
                      disabled={busyId === a.id}
                      style={{ ...secondaryBtn, opacity: busyId === a.id ? 0.6 : 1 }}
                    >
                      {busyId === a.id ? '…' : 'Opnieuw uitvoeren'}
                    </button>
                  )}
                  {mayRun && (
                    <button
                      type="button"
                      onClick={() => void handleDelete(a.id, a.title)}
                      disabled={busyId === a.id}
                      title="Analyse verwijderen"
                      aria-label={`Analyse ${a.title} verwijderen`}
                      style={{ ...secondaryBtn, color: theme.color.subtle }}
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
